import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: SelectOption[];
  /** Rendered as a disabled first option when the field has no value yet. */
  placeholder?: string;
  hasError?: boolean;
}

/**
 * Native `<select>`, styled to match `Input`.
 *
 * Native on purpose: on a tablet at the counter it opens the platform picker, which is faster and
 * more reliable than any custom listbox — and it is accessible without extra work.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, hasError, className, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={hasError ? true : undefined}
      className={cn(
        'border-line w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
        'focus:border-brand-500 focus:ring-brand-100 focus:ring-4 focus:outline-none',
        'disabled:bg-canvas disabled:cursor-not-allowed',
        hasError && 'border-red-400 focus:border-red-500 focus:ring-red-100',
        className,
      )}
      {...props}
    >
      {placeholder ? (
        <option value="" disabled>
          {placeholder}
        </option>
      ) : null}

      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
});
